import React, { useState } from 'react';
import { toast } from 'sonner';
import { FileField } from '@trellixio/roaster-coffee';
import { useQuery, useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query';
import { useAPI, useGetAgency } from 'hooks';
import Page from 'components/shared/Page';
import Locales from 'locales';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import LicenseBlock from './components/LicenseBlock';
import MergerBlock from './components/MergerBlock';

const CONST_MERGED = 'merged';
const CONST_REQUESTING = 'requesting';

const Authorization = () => {
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const isMutating = useIsMutating() > 0;
  const [badge, setBadge] = useState(null);

  const { data: agencyList } = useQuery({
    queryFn: api.list('agency/'),
    queryKey: ['agency/'],
  });

  const { data: currentAgencyArray } = useQuery({
    queryFn: api.list('agency/', { agency: 'is_sa' }),
    queryKey: ['agency/', 'is_sa'],
  });

  const currentAgency = currentAgencyArray ? currentAgencyArray[0] : {};

  const { mergerRequested, mergerApproved, agencyBadge, agencyName, agencyId } = useGetAgency(currentAgency);

  const sortedAgencyList = agencyList
    ? agencyList.filter((agency) => agency.id !== agencyId && agency.merger.approved === null)
    : [];

  const approvedMerges = agencyList
    ? agencyList
        .filter(
          (agency) => agency.merger && agency.merger.master_name === agencyName && agency.merger.approved !== null
        )
        .map((agency) => ({
          id: agency.id,
          name: agency.name,
          tripCount: agency.merger.trip_count,
          created: agency.created,
          type: CONST_MERGED,
        }))
    : [];

  const requestedMerges = agencyList
    ? agencyList
        .filter(
          (agency) =>
            agency.merger &&
            agency.merger.master_name === agencyName &&
            agency.merger.requested !== null &&
            agency.merger.approved === null
        )
        .map((agency) => ({
          id: agency.id,
          name: agency.name,
          tripCount: agency.merger.trip_count,
          created: agency.created,
          type: CONST_REQUESTING,
        }))
    : [];

  const combinedAgenciesArray = [...approvedMerges, ...requestedMerges].sort(
    (a, b) => new Date(a.created) - new Date(b.created)
  );

  const { mutate: addBadge } = useMutation({
    mutationFn: () => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return api.post('agency/file/', { file: badge, object_name: badge.name }, { headers });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(Locales.authorization.toast.add_badge);
    },
  });

  const handleAddBadge = () => {
    if (!badge) {
      toast.error(Locales.authorization.toast.error);
      return;
    }
    addBadge();
  };

  return (
    <Page title={Locales.authorization.title} bodyClassName="max-w-[720px]" removeContentStyles>
      {agencyList && (
        <form className="space-y-6">
          <LicenseBlock currentAgency={currentAgency} />

          <Card>
            <CardHeader>
              <CardTitle>
                <span className="text-xl font-bold">{Locales.authorization.badge}</span>
              </CardTitle>

              {mergerRequested && (
                <CardDescription>
                  <span className="text-gray-600">
                    {mergerApproved
                      ? Locales.authorization.alert.merger_approved
                      : Locales.authorization.alert.merger_pending}
                  </span>
                </CardDescription>
              )}
            </CardHeader>
            {!mergerRequested && (
              <>
                <CardContent>
                  <FileField
                    label={Locales.authorization.badge_selection}
                    size="small"
                    type="image"
                    defaultValue={agencyBadge}
                    accept="image/*"
                    onChange={setBadge}
                  />
                </CardContent>
                <CardFooter>
                  <Button
                    variant="hajj"
                    type="button"
                    className="w-full"
                    disabled={isMutating || badge === null}
                    onClick={() => handleAddBadge()}
                  >
                    {Locales.authorization.save_badge}
                  </Button>
                </CardFooter>
              </>
            )}
          </Card>

          <MergerBlock
            currentAgency={currentAgency}
            sortedAgencyList={sortedAgencyList}
            combinedAgenciesArray={combinedAgenciesArray}
          />
        </form>
      )}
    </Page>
  );
};

export default Authorization;
